<template>
  <div class="content container-fluid">
    <!-- Page Header -->
    <div class="page-header">
      <div class="row align-items-center">
        <div class="col">
          <h3 class="page-title">
            {{ role == "admin" ? "FWA Report" : "My Staff FWA Report" }}
          </h3>
        </div>
      </div>
    </div>
    <!-- /Page Header -->

    <!-- Filter -->
    <div class="row align-items-center">
      <div class="col-md-2 w-170">Company</div>
      <div class="col-md-4 p-0 w-330">
        <select class="custom-select" v-model="company">
          <option
            v-for="(item, i) in companies"
            :key="i"
            :value="item.CompanyName"
          >
            {{ item.CompanyName }}
          </option>
        </select>
      </div>
      <div class="col-md-2 ml-md-5 w-175">Employee Name/ID</div>
      <div class="col-md-4 pl-md-0">
        <Select2 v-model="userId" :options="employees" />
      </div>
    </div>

    <div class="row align-items-center mt-2" v-if="false">
      <div class="col-md-2 w-170">1st Level Approver</div>
      <div class="col-md-4 p-0 w-330">
        <template v-if="isManager && !isNextManager">
          {{ tokenObj.Name }} ({{ tokenObj.EmployeeID }})
        </template>
        <template v-else>
          <Select2
            v-model="firstLevelApproverUserId"
            :options="firstLevelApprovers"
          />
        </template>
      </div>
      <div class="col-md-2 ml-md-5 w-175">2nd Level Approver</div>
      <div class="col-md-4 pl-md-0">
        <template v-if="isNextManager">
          {{ tokenObj.Name }} ({{ tokenObj.EmployeeID }})
        </template>
        <template v-else>
          <Select2
            v-model="secondLevelApproverUserId"
            :options="secondLevelApprovers"
          />
        </template>
      </div>
    </div>

    <div class="row align-items-center mt-2">
      <div class="col-md-2 w-170">Year</div>
      <div class="col-md-2 p-0 fit-content">
        <select class="custom-select" v-model="year">
          <option value>All</option>
          <option v-for="year in years" :key="year" :value="year">
            {{ year }}
          </option>
        </select>
      </div>
      <div class="col-md-2 ml-md-4 fit-content">Date From</div>
      <div class="col-md-2 p-0 fit-content">
        <input type="date" class="form-control" v-model="dateFrom" />
      </div>
      <div class="col-md-2 ml-17 fit-content">Date To</div>
      <div class="col-md-2 p-0 fit-content">
        <input type="date" class="form-control" v-model="dateTo" />
      </div>
    </div>

    <div class="row align-items-center mt-2">
      <div class="col-md-2 w-170">Status</div>
      <div class="col-md-4 p-0 w-330">
        <select class="custom-select" v-model="status">
          <option value>All</option>
          <option value="Pending Approval">Pending Approval</option>
          <option value="Withdrawn">Withdrawn</option>
          <option value="Approved">Approved</option>
          <option value="Rejected">Rejected</option>
          <option value="Pending Cancellation">Pending Cancellation</option>
          <option value="Cancelled">Cancelled</option>
        </select>
      </div>
      <div class="col-md-1 ml-md-5">
        <button type="button" class="btn btn-primary" @click="getFwaReport">
          Search
        </button>
      </div>
      <div class="col-md-3">
        <form
          :action="backendUrl + '/FwaExcelReport/ExportExcel'"
          method="POST"
        >
          <input type="hidden" name="Company" :value="company" />
          <input type="hidden" name="UserId" :value="userId" />
          <input type="hidden" name="Year" :value="year" />
          <input type="hidden" name="DateFrom" :value="dateFrom" />
          <input type="hidden" name="DateTo" :value="dateTo" />
          <input
            type="hidden"
            name="FirstLevelApproverUserId"
            :value="
              isManager && !isNextManager
                ? tokenObj.Id
                : firstLevelApproverUserId
            "
          />
          <input
            type="hidden"
            name="SecondLevelApproverUserId"
            :value="isNextManager ? tokenObj.Id : secondLevelApproverUserId"
          />
          <input type="hidden" name="Status" :value="status" />
          <input type="hidden" name="Token" :value="token" />
          <button type="submit" class="btn btn-primary">
            <img
              width="20px"
              height="20px"
              class="mr-1"
              src="../../assets/excel.png"
            />Export to Excel
          </button>
        </form>
      </div>
    </div>
    <!-- /Filter -->

    <!-- Pagination -->
    <div class="row mt-4" v-if="records && records.length">
      <div class="col-md-6">
        Show
        <select
          class="custom-select"
          v-model="paging.SizePerPage"
          style="width: 5em"
        >
          <option
            v-for="item in paging.Sizes"
            v-bind:key="item"
            v-bind:value="item"
          >
            {{ item }}
          </option>
        </select>
        entries
      </div>
    </div>
    <!-- /Pagination -->

    <!-- Result Empty -->
    <div class="mt-4" v-if="records && records.length == 0">
      <div class="col-12 no-record">
        {{ noRecordText }}
      </div>
    </div>
    <!-- /Result Empty -->

    <!-- Table List -->
    <div class="row mt-2" v-if="records && records.length">
      <div class="col-md-12">
        <div class="table-responsive mb-lg-0 mb-md-0 mb-sm-1">
          <table class="table table-bordered custom-table mb-0">
            <thead class="thead-light">
              <tr>
                <th>Employee ID</th>
                <th>Employee Name</th>
                <th>Position Title</th>
                <th>Org Unit</th>
                <th>1st Level Approver</th>
                <th>2nd Level Approver</th>
                <th>Date of Application</th>
                <th>FWA Type</th>
                <th>FWA Start Date</th>
                <th>FWA End Date</th>
                <th>Total FWA Duration</th>
                <th>Occurrence Per Week</th>
                <th>Impacted Days</th>
                <th>Location</th>
                <th>Flexi Time - Work Day Start Time</th>
                <th>Flexi Time - Work Day End Time</th>
                <th>
                  Flexi Time - Total Working Hours<br />(Including 1 Hour Break)
                </th>
                <th>Flexi Place FWA Reason</th>
                <th>Affected by Flexi Place FWA</th>
                <th>Managing impact of Flexi Place FWA</th>
                <th>Flexi Time FWA Reason</th>
                <th>Affected by Flexi Time FWA</th>
                <th>Managing Impact of Flexi Time FWA</th>
                <th>Employee Acknowledgement - FWA Guidelines</th>
                <th>Employee Acknowledgement - Home refers to Singapore</th>
                <th>1st Manager Approval Justification</th>
                <th>1st Manager Alignment on Work Deliverables</th>
                <th>1st Manager Rejection Justification</th>
                <th>2nd Manager Approval Justification</th>
                <th>2nd Manager Rejection Justification</th>
                <th>1st Level Approver Acknowledgement</th>
                <th>2nd Level Approver Acknowledgement</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in records" :key="item.Id">
                <td>{{ item.EmployeeID }}</td>
                <td>{{ item.EmployeeName }}</td>
                <td>{{ item.PositionTitle }}</td>
                <td>{{ item.CostCenter }}</td>
                <td>
                  {{
                    item.FirstLevelApproverEmployeeName &&
                    item.FirstLevelApproverEmployeeID
                      ? `${item.FirstLevelApproverEmployeeName} (${item.FirstLevelApproverEmployeeID})`
                      : ""
                  }}
                </td>
                <td>
                  {{
                    item.SecondLevelApproverEmployeeName &&
                    item.SecondLevelApproverEmployeeID
                      ? `${item.SecondLevelApproverEmployeeName} (${item.SecondLevelApproverEmployeeID})`
                      : ""
                  }}
                </td>
                <td>{{ item.ApplicationDateDisplayTable }}</td>
                <td>{{ item.Type }}</td>
                <td>{{ item.StartDateDisplay }}</td>
                <td>{{ item.EndDateDisplay }}</td>
                <td>{{ item.Duration }}</td>
                <td>{{ item.OccurrencePerWeek }}</td>
                <td>{{ item.ImpactedDaysDisplay }}</td>
                <td v-html="item.LocationDisplay"></td>
                <td>{{ item.StartTime }}</td>
                <td>{{ item.EndTime }}</td>
                <td>{{ item.WorkingHours }}</td>
                <td v-html="item.FlexiPlaceReasonDisplay"></td>
                <td v-html="item.FlexiPlaceAffectedJobDisplay"></td>
                <td v-html="item.FlexiPlaceProposalDisplay"></td>
                <td v-html="item.FlexiTimeReasonDisplay"></td>
                <td v-html="item.FlexiTimeAffectedJobDisplay"></td>
                <td v-html="item.FlexiTimeProposalDisplay"></td>
                <td>{{ item.IsGuidelineAcknowledgedDisplay }}</td>
                <td>{{ item.IsDefinitionAcknowledgedDisplay }}</td>
                <td v-html="item.FirstLevelApprovalJustificationDisplay"></td>
                <td v-html="item.WorkExpectationDisplay"></td>
                <td v-html="item.FirstLevelRejectionJustificationDisplay"></td>
                <td v-html="item.SecondLevelApprovalJustificationDisplay"></td>
                <td v-html="item.SecondLevelRejectionJustificationDisplay"></td>
                <td>{{ item.IsReplacementInabilityAcknowledgedDisplay }}</td>
                <td>{{ item.IsDecisionAcknowledgedDisplay }}</td>
                <td>{{ item.Status }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <!-- /Table List -->

    <!--Pagination -->
    <div class="row mb-4" v-if="records && records.length">
      <div class="col-md-6 col-lg-6 col-sm-6 my-auto text-lg-left text-center">
        Showing
        {{ paging.CurrentPage * paging.SizePerPage - paging.SizePerPage + 1 }}
        to
        {{
          paging.CurrentPage * paging.SizePerPage -
          paging.SizePerPage +
          records.length
        }}
        of {{ totalRecords }} entries
      </div>

      <div class="col-md-6 col-lg-6 col-sm-6 pr-md-0 pr-lg-0">
        <div class="navbar justify-content-end">
          <button
            v-on:click="first()"
            v-if="paging.CurrentPage != 1"
            class="btn btn-white"
          >
            First
          </button>
          <button
            v-on:click="prev()"
            class="btn btn-white ml-1 mr-3"
            v-if="paging.CurrentPage > 1"
          >
            Prev
          </button>
          Page
          <input
            class="form-control form-control-sm mx-1"
            type="number"
            step="1"
            min="1"
            :max="paging.TotalPage"
            v-model="paging.CurrentPage"
            @change="onCurrentPageChange($event)"
            style="width: 4rem"
          />

          <span>of {{ paging.TotalPage }}</span>
          <button
            v-on:click="next()"
            class="btn btn-white ml-3 mr-1"
            v-if="paging.CurrentPage < paging.TotalPage"
          >
            Next
          </button>
          <button
            v-on:click="last()"
            v-if="paging.CurrentPage != paging.TotalPage"
            class="btn btn-white"
          >
            Last
          </button>
        </div>
      </div>
    </div>
    <!--/Pagination -->
  </div>
</template>

<script>
import API from "../../http-common.js";
import config from "../../config.js";
export default {
  data() {
    return {
      noRecordText: config.NO_RECORD_TEXT,
      defaultCompany: config.DEFAULT_COMPANY,
      records: [],
      company: "",
      companies: [],
      userId: "00000000-0000-0000-0000-000000000000",
      managerEmployees: [],
      nextManagerEmployees: [],
      allEmployees: [],
      year: "",
      dateFrom: "",
      dateTo: "",
      firstLevelApprovers: [],
      firstLevelApproverUserId: "00000000-0000-0000-0000-000000000000",
      secondLevelApprovers: [],
      secondLevelApproverUserId: "00000000-0000-0000-0000-000000000000",
      status: "",
      fwaYears: [],
      //paging
      paging: {
        CurrentPage: 1,
        TotalPage: "",
        SizePerPage: 100,
        Sizes: [5, 10, 20, 100],
      },
      totalRecords: "",
    };
  },
  computed: {
    token() {
      let data = JSON.parse(localStorage.getItem("lambitoken"));
      return data.Token;
    },
    tokenObj() {
      let data = JSON.parse(localStorage.getItem("lambitoken"));
      return data;
    },
    role() {
      let data = JSON.parse(localStorage.getItem("lambitoken"));
      return data.Role;
    },
    isManager() {
      let data = JSON.parse(localStorage.getItem("lambitoken"));
      return data.IsManager ? data.IsManager : false;
    },
    isNextManager() {
      let data = JSON.parse(localStorage.getItem("lambitoken"));
      return data.IsNextManager ? data.IsNextManager : false;
    },
    years() {
      let date = new Date();
      let minYear = date.getFullYear() - 2;
      minYear =
        this.fwaYears.length > 0 && this.fwaYears[0] < minYear
          ? this.fwaYears[0]
          : minYear;
      let maxYear = date.getFullYear() + 1;
      maxYear =
        this.fwaYears.length > 0 &&
        this.fwaYears[this.fwaYears.length - 1] > maxYear
          ? this.fwaYears[this.fwaYears.length - 1]
          : maxYear;
      let years = [];
      for (let year = maxYear; year > minYear; year--) {
        years.push(year);
      }
      return years;
    },
    backendUrl() {
      return this.$backendUrl;
    },
    employees() {
      let employees = [];
      if (this.role == "admin") employees = this.allEmployees;
      else {
        let combinedEmployees = this.managerEmployees.concat(
          this.nextManagerEmployees
        );
        //remove duplicate
        employees = combinedEmployees.filter(
          (obj, index, self) => index === self.findIndex((o) => o.id === obj.id)
        );
      }
      employees.sort((a, b) => a.text.localeCompare(b.text));
      employees.unshift({
        department: "",
        id: "00000000-0000-0000-0000-000000000000",
        text: "All",
        Company: "",
      });
      return employees;
    },
  },
  watch: {
    "paging.SizePerPage": function (val, oldVal) {
      this.paging.CurrentPage = 1;
      this.getFwaReport();
    },
    "paging.CurrentPage": function (val, oldVal) {
      if (val > this.paging.TotalPage) {
        this.paging.CurrentPage = this.paging.TotalPage;
      }
    },
    company(val) {
      //get employee list
      if (this.role == "admin") {
        this.getEmployees();
      } else {
        if (this.isManager) this.getManagerEmployees();
        if (this.isNextManager) this.getNextManagerEmployees();
      }
      //get manager list
      this.getAllFwaApprovers();
    },
  },
  methods: {
    //filter
    getCompanies() {
      let param = {
        Token: this.token,
      };
      this.$store.state.isLoading = true;
      API.getCompanies(param)
        .then((response) => {
          this.$store.state.isLoading = false;
          if (response.data.success) {
            this.companies = response.data.record;
            if (
              this.companies.find(
                (c) => c.CompanyName == this.defaultCompany
              ) == null
            ) {
              this.companies.push({ CompanyName: this.defaultCompany });
            }
            this.company = this.defaultCompany;
            this.getFwaReport();
          } else {
            this.$swal(response.data.message, { icon: "error" });
          }
        })
        .catch((error) => {
          this.$store.state.isLoading = false;
          this.$swal(error.response.data.Message, { icon: "error" });
        });
    },
    getManagerEmployees() {
      let param = {
        Token: this.token,
        ManagerID: this.tokenObj.EmployeeID,
        Company: this.company,
      };
      this.$store.state.isLoading = true;
      API.getManagerEmployees(param)
        .then((response) => {
          this.$store.state.isLoading = false;
          if (response.data.success) {
            this.managerEmployees = response.data.record;
          } else {
            this.$swal(response.data.message, { icon: "error" });
          }
        })
        .catch((error) => {
          this.$store.state.isLoading = false;
          this.$swal(error.response.data.Message, { icon: "error" });
        });
    },
    getNextManagerEmployees() {
      let param = {
        Token: this.token,
        ManagerID: this.tokenObj.EmployeeID,
        Company: this.company,
      };
      this.$store.state.isLoading = true;
      API.getNextManagerEmployees(param)
        .then((response) => {
          this.$store.state.isLoading = false;
          if (response.data.success) {
            this.nextManagerEmployees = response.data.record;
          } else {
            this.$swal(response.data.message, { icon: "error" });
          }
        })
        .catch((error) => {
          this.$store.state.isLoading = false;
          this.$swal(error.response.data.Message, { icon: "error" });
        });
    },
    getEmployees() {
      let param = { Token: this.token, Company: this.company };
      this.$store.state.isLoading = true;
      API.getEmployees(param)
        .then((response) => {
          this.$store.state.isLoading = false;
          if (response.data.success) {
            this.allEmployees = response.data.record;
          } else {
            this.$swal(response.data.message, { icon: "error" });
          }
        })
        .catch((error) => {
          this.$store.state.isLoading = false;
          this.$swal(error.response.data.Message, { icon: "error" });
        });
    },
    getAllFwaApprovers() {
      let param = {
        Token: this.token,
        Company: this.company,
      };
      this.$store.state.isLoading = true;
      API.getAllFwaApprovers(param)
        .then((response) => {
          this.$store.state.isLoading = false;
          if (response.data.success) {
            this.firstLevelApprovers = response.data.firstLevelApprovers;
            this.firstLevelApprovers.unshift({
              id: "00000000-0000-0000-0000-000000000000",
              text: "All",
            });
            this.secondLevelApprovers = response.data.secondLevelApprovers;
            this.secondLevelApprovers.unshift({
              id: "00000000-0000-0000-0000-000000000000",
              text: "All",
            });
          } else {
            this.$swal(response.data.message, { icon: "error" });
          }
        })
        .catch((error) => {
          this.$store.state.isLoading = false;
          this.$swal(error.response.data.Message, { icon: "error" });
        });
    },
    //main function
    getFwaReport() {
      let param = {
        Token: this.token,
        CurrentPage: this.paging.CurrentPage,
        TotalPage: this.paging.TotalPage,
        SizePerPage: this.paging.SizePerPage,
        Company: this.company,
        UserId: this.userId,
        Year: this.year,
        DateFrom: this.dateFrom,
        DateTo: this.dateTo,
        FirstLevelApproverUserId:
          this.isManager && !this.isNextManager
            ? this.tokenObj.Id
            : this.firstLevelApproverUserId,
        SecondLevelApproverUserId: this.isNextManager
          ? this.tokenObj.Id
          : this.secondLevelApproverUserId,
        Status: this.status,
      };
      this.$store.state.isLoading = true;
      API.getFwaReport(param)
        .then((response) => {
          this.$store.state.isLoading = false;
          if (response.data.success) {
            this.records = response.data.record;
            this.totalRecords = response.data.total;
            this.paging.TotalPage = response.data.pageCount;
            this.fwaYears = response.data.fwaYears;
            if (this.paging.CurrentPage > this.paging.TotalPage) {
              this.paging.CurrentPage = 1;
            }
          } else {
            this.$swal(response.data.message, { icon: "error" });
          }
        })
        .catch((error) => {
          this.$store.state.isLoading = false;
          this.$swal(error.response.data.Message, { icon: "error" });
        });
    },
    //pagination method
    onCurrentPageChange(event) {
      let currentPage = event.target.value;
      if (currentPage < 1) {
        currentPage = 1;
      }
      this.paging.CurrentPage = parseInt(currentPage);
      this.getFwaReport();
    },
    first() {
      this.paging.CurrentPage = 1;
      this.getFwaReport();
    },
    prev() {
      this.paging.CurrentPage -= 1;
      this.getFwaReport();
    },
    next() {
      this.paging.CurrentPage += 1;
      this.getFwaReport();
    },
    last() {
      this.paging.CurrentPage = this.paging.TotalPage;
      this.getFwaReport();
    },
  },
  created() {
    this.getCompanies();
  },
};
</script>

<style scoped>
/* .table-responsive {
  white-space: unset;
} */
table th {
  text-align: center;
}
table th,
table td {
  word-spacing: -2px;
}
.fit-content {
  max-width: fit-content;
}
.w-170 {
  max-width: 170px;
}
.w-175 {
  max-width: 175px;
}
.w-330 {
  max-width: 330px;
}
.ml-17 {
  margin-left: 17px;
}
@media only screen and (max-width: 1400px) {
  .w-170,
  .w-175 {
    max-width: 100px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Fwa/FwaReport.vue